/**
 * ProDetailSlideOver — right-edge slide-out panel that renders the same
 * Pro detail content as /pro/:pronumber, complete with edit + save.
 *
 * Portaled to document.body so parent stacking contexts (transforms,
 * filters, will-change) can't pin the panel behind page chrome. The
 * panel sits above the global SyncStatusFooter and at the same z-index
 * as the nested modals (DriverPayAdditionModal etc.); same-z + DOM
 * order means a modal opened from inside the drawer correctly covers
 * the drawer.
 *
 * Left edge has a 4px drag handle that resizes the panel. The chosen
 * width is persisted per-user via userSettingsService and rehydrated
 * the next time the drawer opens.
 */
import ActiveMoveShareLink from './ActiveMoveShareLink';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import ProDetailContainer from './ProDetailContainer';
import { isCreateSentinel } from './createSentinels';
import type { ProjectedStatus } from '../driver-status-monitor/types';
import ordersService, { type OrderDetails } from '../../services/orders.service';
import type { StagedDoc } from '../../pages/pro/createOrder/stagedDocs';
import ProMarginModal from './ProMarginModal';
import type { ProMargin } from '../../services/proDocuments.service';
import WarningConfirmModal from '../ui/WarningConfirmModal';
import userSettingsService from '../../services/userSettings.service';
import { activityTracker } from '../../services/activity.service';
import { config } from '../../config/config';
import { isTemplatePronumber } from '../../pages/pro/templateFieldPolicy';
import { masterproSearchPath } from '../../utils/proSearch';

/** Pronumber sentinel the create flow opens the drawer on, until the backend
 *  allocates the real one. Non-numeric on purpose: every numeric-pronumber gate
 *  downstream (masterpro siblings, trace buttons, margin/ETA) stays off. */
// Re-exported so existing importers keep working; they live in their own module
// because ProDetailContainer needs them as values too (see createSentinels.ts).
export {
  CREATE_PRONUMBER_SENTINEL,
  CREATE_TEMPLATE_PRONUMBER_SENTINEL,
  isCreateSentinel,
} from './createSentinels';

/** The modal's authoritative field set, forwarded verbatim into the create
 *  payload (the server applies it over the template copy). */
export type CreateOrderSpec = {
  /** Which record the flow is creating. Selects the endpoint at save time and the
   *  sentinel the drawer sits on until then. */
  kind: 'order' | 'template';
  movetype: string;
  billto: number;
  customercode: number;
  /** Order-only: a template has no Bkg/BOL or container (a blueprint carries
   *  neither), so the modal does not collect them in template mode. */
  bol: string;
  container: string;
  templatepro?: string;
  bypassDupeCheck: boolean;
};

/**
 * Create New Order mode. Owned by the create-flow provider: the drawer edits
 * the seed through the ordinary editing hook, while the staging surfaces that
 * live OUTSIDE that hook (docs, notes) stay in flow state so they survive tab
 * unmounts. `onCreated` runs after the order commits (staged-doc replay) and
 * the flow then flips `pronumber` to the real number.
 */
export type ProDetailCreate = {
  seed: OrderDetails;
  spec: CreateOrderSpec;
  stagedDocs: StagedDoc[];
  onStagedDocsChange: (next: StagedDoc[]) => void;
  stagedNotes: string[];
  onStagedNotesChange: (next: string[]) => void;
  /** `documenttype` of the picked template's docs (copied by reference at
   *  create), `[]` with no template, or null while unknown — feeds the
   *  Delivery-Order-required gate, which fails OPEN on null. */
  templateDocTypes: string[] | null;
  /** A template's allocated pronumber is a "T…" string, not a number. */
  onCreated: (pronumber: number | string) => Promise<void>;
};

type Props = {
  // Templates ("T110") ride the same drawer as numeric PROs, so this accepts
  // either. It's forwarded to ProDetailContainer (which accepts number | string)
  // and only ever used via `== null` guards + String(...) elsewhere.
  pronumber: number | string | null;
  isOpen: boolean;
  onClose: () => void;
  /** When set, opens the drawer in Change-All mode: `targetPros` is the bulk
   *  target set and `pronumber` should be the first of them (the anchor). */
  changeAll?: { targetPros: (number | string)[] } | null;
  /** Create New Order mode — active while `pronumber` is the 'NEW' sentinel. */
  create?: ProDetailCreate | null;
  /** Tab a newly opened PRO lands on (drawer default: order-info). */
  initialTab?: string;
};

const SETTING_KEY = 'pro_drawer_width';
const LS_KEY = 'proDrawerWidth';
const SNAP_LS_KEY = 'proDrawerSnappedToNav';
const MIN_WIDTH = 400;
const FALLBACK_NAV_RIGHT = 80; // used when no [data-app-nav] is found
const SNAP_THRESHOLD = 24; // px — how close before snapping
const DEFAULT_RATIO = 0.7; // first-ever open

// Header ETA pill colors, keyed by the projected on-time status of the active
// leg. Mirrors the driver-status-monitor palette.
const ETA_PILL_CLASS: Record<ProjectedStatus, string> = {
  on_track: 'bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700',
  at_risk: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700',
  will_miss: 'bg-red-50 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700',
  missed: 'bg-red-100 text-red-900 border-red-300 line-through dark:bg-red-900/50 dark:text-red-200 dark:border-red-700',
};

/** Right edge (x) of the app nav, in viewport coords. Falls back to a fixed
 *  gutter when no nav is mounted (e.g. login screens). */
const getNavRight = (): number => {
  const el = document.querySelector<HTMLElement>('[data-app-nav]');
  if (!el) return FALLBACK_NAV_RIGHT;
  return Math.max(0, el.getBoundingClientRect().right);
};

const maxWidthForNav = (navRight: number) =>
  Math.max(MIN_WIDTH, window.innerWidth - navRight);

const computeDefault = () =>
  Math.max(MIN_WIDTH, Math.min(maxWidthForNav(getNavRight()), window.innerWidth * DEFAULT_RATIO));

const clampWidth = (n: number) =>
  Math.max(MIN_WIDTH, Math.min(maxWidthForNav(getNavRight()), n));

const readLocalWidth = (): number | null => {
  try {
    const v = localStorage.getItem(LS_KEY);
    if (!v) return null;
    const n = parseInt(v, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
};

const readSnapped = (): boolean => {
  try {
    return localStorage.getItem(SNAP_LS_KEY) === 'true';
  } catch {
    return false;
  }
};

const ProDetailSlideOver: React.FC<Props> = ({
  pronumber,
  isOpen,
  onClose,
  changeAll = null,
  create = null,
  initialTab,
}) => {
  // A numeric PRO can reach us as a STRING — Change All builds its target set with
  // String(pronumber) and passes the first as the anchor. Downstream editability
  // gates key off `typeof pronumber === 'number'` (InvoicingView.editable, the
  // LC-field gates, LegsGrid write affordances), so a numeric-string anchor would
  // render the whole drawer read-only in Change All mode. Coerce a pure-numeric
  // string to a number so Change All edits exactly like the single-pro drawer;
  // templates ("T110") and null pass through untouched.
  const normalizedPronumber = useMemo<number | string | null>(
    () =>
      typeof pronumber === 'string' && /^\d+$/.test(pronumber.trim())
        ? Number(pronumber)
        : pronumber,
    [pronumber],
  );
  // Master Pro navigation: the drawer keeps its own "active" pronumber so the
  // prev/next arrows can walk the masterpro group without every caller wiring
  // up navigation. It initializes from the prop and resets whenever the parent
  // opens a different PRO. Mirrors the prop's type so template PROs ("T110")
  // ride along; numeric-only sibling nav is gated on `typeof === 'number'`.
  const [activePro, setActivePro] = useState<number | string | null>(normalizedPronumber);
  useEffect(() => {
    setActivePro(normalizedPronumber);
  }, [normalizedPronumber]);

  // Create mode is live only while the drawer sits on the 'NEW' sentinel. Once
  // the flow flips `pronumber` to the allocated pro, every branch below (and the
  // editing hook itself) falls back to the ordinary open-pro behavior.
  const isCreating = !!create && isCreateSentinel(activePro);
  // A template is a blueprint, not a pro — say so everywhere the drawer names the
  // record (header + both unsaved-changes prompts). One value so they can't drift.
  const recordLabel = isTemplatePronumber(activePro) ? 'TEMPLATE' : 'PRO';
  // Prose form of the same distinction, for the create-mode copy below. Resolves
  // off the sentinel too: 'TNEW' is "T"-prefixed precisely so it does (#33471).
  const recordNoun = isTemplatePronumber(activePro) ? 'template' : 'order';

  // Ordered PRO numbers sharing activePro's masterpro (empty when ungrouped).
  const [siblings, setSiblings] = useState<{ masterPro: string; pronumbers: number[] }>({
    masterPro: '',
    pronumbers: [],
  });
  useEffect(() => {
    // Templates ("T110") have no numeric masterpro group, so skip the fetch.
    if (!isOpen || typeof activePro !== 'number') return;
    let cancelled = false;
    ordersService
      .getMasterproSiblings(activePro)
      .then((res) => {
        if (!cancelled) setSiblings(res);
      })
      .catch(() => {
        if (!cancelled) setSiblings({ masterPro: '', pronumbers: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, activePro]);

  // Unsaved-changes flag lifted from the inner container, plus the pending
  // navigation target held while we confirm discarding those changes.
  const [dirty, setDirty] = useState(false);
  // Live mirror so the memoized `guardedClose` (below) reads the current
  // order-edit dirty state without being re-created on every dirty flip.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const [pendingNav, setPendingNav] = useState<number | null>(null);
  // A drawer-close we're holding until the user resolves the order-edit
  // unsaved-changes prompt (Discard & Close / Stay).
  const [pendingClose, setPendingClose] = useState(false);

  // Closing the drawer (X / Escape / footer Close / auto-close-on-save) is
  // guarded against losing unsaved work. Invoicing edits now live in the shared
  // editing hook (editedOrderFields + line-item draft), so they're already
  // reflected in `dirty` — the single order-edit prompt covers them too.
  // `skipOrderEditGuard` is set only by the post-save auto-close: the save just
  // cleared the edits, but the lifted `dirty` flag hasn't propagated up yet, so
  // checking it would falsely prompt. Stable across renders (only depends on
  // onClose) so the Escape key effect below doesn't re-subscribe every render —
  // it reads the live dirty via ref.
  const guardedClose = useCallback(
    (opts?: { skipOrderEditGuard?: boolean }) => {
      if (opts?.skipOrderEditGuard !== true && dirtyRef.current) {
        setPendingClose(true);
      } else {
        onClose();
      }
    },
    [onClose],
  );

  // Initial width is read synchronously from localStorage to avoid a flash
  // from default → saved on every drawer open. The server-side user-settings
  // copy is a one-time cross-device backfill below.
  const [snappedToNav, setSnappedToNav] = useState<boolean>(() => readSnapped());
  const snappedRef = useRef(snappedToNav);
  snappedRef.current = snappedToNav;

  const [width, setWidth] = useState<number>(() =>
    clampWidth(readLocalWidth() ?? computeDefault()),
  );
  const widthRef = useRef(width);
  widthRef.current = width;

  // The drawer panel is hidden until we know the user's saved width, so it
  // doesn't flash at a default size before the saved size lands. If we have
  // a cached width in localStorage, settings are immediately ready;
  // otherwise we wait for the server lookup below to finish.
  const [settingsReady, setSettingsReady] = useState<boolean>(() => readLocalWidth() != null);

  // While snapped, keep the drawer flush with the nav as the nav resizes
  // (e.g. collapse/expand toggle, window resize). We deliberately do NOT
  // re-snap on initial open — the saved width is the source of truth at
  // Activity tracking: one pro_view event per drawer open.
  useEffect(() => {
    if (!isOpen || activePro == null) return;
    // Nothing to view yet on an uncreated order — don't log the sentinel.
    if (isCreating) return;
    activityTracker.track({ event_type: 'pro_view', detail: String(activePro) });
  }, [isOpen, activePro, isCreating]);

  // open time, so the user gets back exactly what they last set without a
  // flash from an intermediate max-width render.
  useEffect(() => {
    if (!isOpen) return;
    const nav = document.querySelector<HTMLElement>('[data-app-nav]');
    const sync = () => {
      if (!snappedRef.current) return;
      setWidth(maxWidthForNav(getNavRight()));
    };
    let ro: ResizeObserver | null = null;
    if (nav && typeof ResizeObserver !== 'undefined') {
      let firstObserve = true; // ResizeObserver always fires once on attach
      ro = new ResizeObserver(() => {
        if (firstObserve) {
          firstObserve = false;
          return;
        }
        sync();
      });
      ro.observe(nav);
    }
    window.addEventListener('resize', sync);
    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', sync);
    };
  }, [isOpen, snappedToNav]);

  // Header info (meta row + movetype / classification badges) is owned by
  // the inner container because it lives on the fetched orderDetails. It's
  // lifted up here so the drawer chrome can render meta + badges inline
  // next to "PRO {pronumber}".
  type HeaderInfo = {
    movetype?: string;
    classification?: string;
    deleted?: boolean;
    equipmentNumber?: string;
    billTo?: { name: string; code?: string | number } | null;
    customer?: { name: string; code?: string | number } | null;
    eta?: { text: string; status: ProjectedStatus } | null;
    liveSharePro?: string;
    liveShareUrl?: string;
    liveShareExpiresAt?: string;
    margin?: { pct: number | null; underLimit: boolean; data: ProMargin | null } | null;
    marginError?: string | null;
    marginLoading?: boolean;
    marginRefresh?: () => void;
  };
  const [badgeInfo, setBadgeInfo] = useState<HeaderInfo>({});
  // Whether the margin breakdown modal (V1 showmargin) is open.
  const [marginModalOpen, setMarginModalOpen] = useState(false);

  // Reset badges when the drawer closes or switches Pros so stale badges
  // don't flash on the next open.
  useEffect(() => {
    if (!isOpen || activePro == null) {
      setBadgeInfo({});
      // Also clear the margin modal — the slide-over stays mounted across
      // close/reopen, so a sticky `true` would auto-open the modal on the
      // next open once badgeInfo repopulates.
      setMarginModalOpen(false);
    }
  }, [isOpen, activePro]);

  // Cross-device sync: only hit the server when localStorage hasn't seen a
  // value yet. After that the local cache is authoritative for load latency.
  useEffect(() => {
    if (readLocalWidth() != null) return;
    let cancelled = false;
    userSettingsService
      .getUserSetting(SETTING_KEY)
      .then((s) => {
        if (cancelled) return;
        const val = (s?.setting_value as { width?: number } | undefined)?.width;
        if (typeof val === 'number' && val > 0) {
          const clamped = clampWidth(val);
          setWidth(clamped);
          try {
            localStorage.setItem(LS_KEY, String(clamped));
          } catch {
            /* ignore quota errors */
          }
        }
      })
      .catch(() => {
        /* best-effort — default width if the lookup fails */
      })
      .finally(() => {
        if (!cancelled) setSettingsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Escape closes (guarded by unsaved invoicing edits).
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') guardedClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, guardedClose]);

  const onHandleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = widthRef.current;
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    const onMove = (ev: MouseEvent) => {
      // Drawer is anchored to the right; dragging the handle leftward
      // (clientX decreases) grows the panel.
      const raw = startWidth + (startX - ev.clientX);
      const navRight = getNavRight();
      const maxW = maxWidthForNav(navRight);
      // Snap when the drawer's left edge gets within SNAP_THRESHOLD of the
      // nav's right edge.
      const snap = raw >= maxW - SNAP_THRESHOLD;
      setWidth(snap ? maxW : Math.max(MIN_WIDTH, Math.min(maxW, raw)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // If we settled at the nav's right edge, mark snapped so future nav
      // resizes drag the drawer along with them.
      const navRight = getNavRight();
      const maxW = maxWidthForNav(navRight);
      const isSnapped = widthRef.current >= maxW - 1;
      setSnappedToNav(isSnapped);
      try {
        localStorage.setItem(LS_KEY, String(widthRef.current));
        localStorage.setItem(SNAP_LS_KEY, String(isSnapped));
      } catch {
        /* ignore quota errors */
      }
      userSettingsService
        .saveUserSetting(SETTING_KEY, { width: widthRef.current })
        .catch(() => {
          /* best-effort */
        });
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Double-click the handle to toggle snap-to-nav. When already snapped,
  // a second double-click restores the previous (or default) width.
  const onHandleDoubleClick = () => {
    if (snappedRef.current) {
      const restored = clampWidth(readLocalWidth() ?? computeDefault());
      const navMax = maxWidthForNav(getNavRight());
      const next = restored >= navMax - 1 ? Math.max(MIN_WIDTH, navMax * 0.6) : restored;
      setSnappedToNav(false);
      setWidth(next);
      try {
        localStorage.setItem(LS_KEY, String(next));
        localStorage.setItem(SNAP_LS_KEY, 'false');
      } catch {
        /* ignore quota errors */
      }
      userSettingsService
        .saveUserSetting(SETTING_KEY, { width: next })
        .catch(() => {
          /* best-effort */
        });
      return;
    }
    const navMax = maxWidthForNav(getNavRight());
    setSnappedToNav(true);
    setWidth(navMax);
    try {
      localStorage.setItem(LS_KEY, String(navMax));
      localStorage.setItem(SNAP_LS_KEY, 'true');
    } catch {
      /* ignore quota errors */
    }
    userSettingsService
      .saveUserSetting(SETTING_KEY, { width: navMax })
      .catch(() => {
        /* best-effort */
      });
  };

  if (!isOpen || activePro == null) return null;
  if (!settingsReady) return null;

  // Color the drawer header by move direction: orange for exports, green
  // for imports, purple for domestic/inbound/outbound. Falls back to the
  // neutral slate header for anything else.
  const mt = (badgeInfo.movetype || '').toUpperCase();
  const isExport = mt.includes('EXPORT') || mt.includes('RAIL TO PORT');
  const isImport = mt.includes('IMPORT');
  const isDomestic =
    mt.includes('DOMESTIC') ||
    mt.includes('INBOUND') ||
    mt.includes('OUTBOUND') ||
    mt.includes('REPO');
  // A deleted record turns the whole header bar red (overriding the move-type
  // color) so it's obvious at a glance, matching the legacy DELETED header.
  const headerClass = badgeInfo.deleted
    ? 'bg-red-200 border-red-400 dark:bg-red-900/40 dark:border-red-700'
    : isExport
    ? 'bg-orange-100 border-orange-300 dark:bg-orange-900/30 dark:border-orange-700'
    : isImport
    ? 'bg-green-100 border-green-300 dark:bg-green-900/30 dark:border-green-700'
    : isDomestic
    ? 'bg-purple-100 border-purple-300 dark:bg-purple-900/30 dark:border-purple-700'
    : 'bg-slate-50 border-slate-200 dark:bg-gray-800 dark:border-gray-700';
  const movetypeBadgeClass = isExport
    ? 'bg-orange-100 text-orange-800 border-orange-500 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-600'
    : isImport
    ? 'bg-green-200 text-green-700 border-green-500 dark:bg-green-900/40 dark:text-green-300 dark:border-green-600'
    : isDomestic
    ? 'bg-purple-200 text-purple-700 border-purple-500 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-600'
    : 'bg-slate-200 text-slate-700 border-slate-400 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600';

  // Master Pro position within the sibling group. Arrows are removed (not just
  // disabled) at the ends, matching the legacy header behavior.
  const idx = typeof activePro === 'number' ? siblings.pronumbers.indexOf(activePro) : -1;
  const total = siblings.pronumbers.length;
  // Master-Pro prev/next sibling nav is meaningless when the drawer is walking
  // a bulk target set, so hide the affordance in Change-All mode.
  const showNav = !changeAll && !!siblings.masterPro && total > 0 && idx >= 0;
  const hasPrev = showNav && idx > 0;
  const hasNext = showNav && idx < total - 1;
  const requestNav = (target: number) => {
    if (dirty) setPendingNav(target);
    else setActivePro(target);
  };

  const node = (
    // pointer-events-none lets clicks land on the page beneath the empty
    // area to the left of the drawer; the panel itself re-enables them.
    // No backdrop and no click-outside-to-close — the drawer is intended
    // to coexist with the page, not modal-block it.
    <div
      className="fixed inset-0 z-[100] pointer-events-none"
      data-testid="pro-detail-slideover"
    >
      <div
        style={{ width }}
        className="absolute right-0 top-0 h-full bg-white dark:bg-gray-900 shadow-xl flex pointer-events-auto"
        role="dialog"
        aria-label={isCreating ? 'New order details' : `PRO ${activePro} details`}
      >
        <div
          className="w-1 bg-slate-200 dark:bg-gray-700 hover:bg-blue-400 dark:hover:bg-blue-500 active:bg-blue-500 cursor-ew-resize shrink-0 transition-colors"
          onMouseDown={onHandleMouseDown}
          onDoubleClick={onHandleDoubleClick}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize panel"
          title="Drag to resize · double-click to snap to nav"
        />
        <div className="flex-1 min-w-0 flex flex-col">
          <div className={`flex items-center justify-between gap-3 px-3 py-1.5 border-b shrink-0 ${headerClass}`}>
            <div className="flex items-center gap-x-4 gap-y-1 flex-wrap min-w-0 text-sm">
              {isCreating ? (
                // No pro number exists yet, and there's nothing for V1 to open.
                <span className="px-2 py-0.5 text-[0.6875rem] font-bold uppercase tracking-wider rounded border bg-blue-100 text-blue-800 border-blue-400 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-600">
                  NEW {recordLabel === 'TEMPLATE' ? 'TEMPLATE' : 'ORDER'}
                </span>
              ) : (
                <span className="flex items-center gap-1.5 text-xs">
                  <span className="text-[0.6875rem] uppercase tracking-wider text-slate-500 dark:text-gray-400">
                    {recordLabel} #
                  </span>
                  <span className="text-base font-bold leading-none text-slate-900 dark:text-gray-100">{activePro}</span>
                  <a
                    href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${activePro}&searchtype=pronumber`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                    title="Open in CMS V1"
                    aria-label="Open in CMS V1"
                  >
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                  </a>
                </span>
              )}
              {badgeInfo.deleted && (
                <span className="px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wider rounded bg-red-600 text-white border border-red-700">
                  Deleted
                </span>
              )}
              {badgeInfo.equipmentNumber && (
                <span className="flex items-center gap-1.5 text-xs">
                  <span className="text-[0.6875rem] uppercase tracking-wider text-slate-500 dark:text-gray-400">Equipment #</span>
                  <span className="font-semibold text-slate-900 dark:text-gray-100">{badgeInfo.equipmentNumber}</span>
                </span>
              )}
              {badgeInfo.billTo && (
                <span className="flex items-center gap-1.5 text-xs">
                  <span className="text-[0.6875rem] uppercase tracking-wider text-slate-500 dark:text-gray-400">Bill To</span>
                  <span className="font-semibold text-slate-900 dark:text-gray-100">{badgeInfo.billTo.name}</span>
                  {badgeInfo.billTo.code != null && badgeInfo.billTo.code !== '' && (
                    <span className="text-slate-400 dark:text-gray-500">{badgeInfo.billTo.code}</span>
                  )}
                </span>
              )}
              {badgeInfo.customer && (
                <span className="flex items-center gap-1.5 text-xs">
                  <span className="text-[0.6875rem] uppercase tracking-wider text-slate-500 dark:text-gray-400">Customer</span>
                  <span className="font-semibold text-slate-900 dark:text-gray-100">{badgeInfo.customer.name}</span>
                  {badgeInfo.customer.code != null && badgeInfo.customer.code !== '' && (
                    <span className="text-slate-400 dark:text-gray-500">{badgeInfo.customer.code}</span>
                  )}
                </span>
              )}
              {badgeInfo.movetype && (
                <span className={`px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wider rounded border ${movetypeBadgeClass}`}>
                  {badgeInfo.movetype}
                </span>
              )}
              {badgeInfo.classification && (
                <span className="px-2 py-0.5 text-[0.625rem] font-bold uppercase tracking-wider rounded bg-indigo-50 text-indigo-800 border border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-700">
                  {badgeInfo.classification}
                </span>
              )}
              {!isCreating && !badgeInfo.deleted && badgeInfo.liveSharePro === String(activePro) && (
                <ActiveMoveShareLink url={badgeInfo.liveShareUrl} expiresAt={badgeInfo.liveShareExpiresAt} />
              )}
              {badgeInfo.eta && (
                <span className="flex items-center gap-1.5 text-xs">
                  <span className="text-[0.6875rem] uppercase tracking-wider text-slate-500 dark:text-gray-400">ETA</span>
                  <span className={`px-2 py-0.5 text-[0.6875rem] font-bold rounded border ${ETA_PILL_CLASS[badgeInfo.eta.status]}`}>{badgeInfo.eta.text}</span>
                </span>
              )}
              {badgeInfo.margin && (
                <span className="flex items-center gap-1.5 text-xs">
                  <span className="text-[0.6875rem] uppercase tracking-wider text-slate-500 dark:text-gray-400">Margin</span>
                  <button
                    type="button"
                    onClick={() => {
                      // Fresh pull so the modal reflects newly-added driver-pay
                      // line items and the header % is up to date.
                      badgeInfo.marginRefresh?.();
                      setMarginModalOpen(true);
                    }}
                    title="View margin breakdown (Customer Pay / Driver Pay / Profit-Loss)"
                    aria-label="View margin breakdown"
                    className={`px-2 py-0.5 text-[0.6875rem] font-bold rounded border cursor-pointer transition-shadow hover:ring-2 hover:ring-offset-1 focus:outline-none focus-visible:ring-2 ${
                      badgeInfo.margin.underLimit
                        ? 'bg-red-100 text-red-800 border-red-400 hover:ring-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-600'
                        : 'bg-emerald-100 text-emerald-800 border-emerald-400 hover:ring-emerald-300 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-600'
                    }`}
                  >
                    {badgeInfo.marginLoading ? 'Loading…' : badgeInfo.margin.pct == null ? 'View' : `${badgeInfo.margin.pct.toFixed(1)}%`}
                  </button>
                </span>
              )}
              {badgeInfo.marginError && <span role="alert" className="text-xs text-red-600">{badgeInfo.marginError}</span>}
              {!badgeInfo.margin && badgeInfo.marginLoading && (
                <span className="flex items-center gap-1.5 text-xs">
                  <span className="text-[0.6875rem] uppercase tracking-wider text-slate-500 dark:text-gray-400">Margin</span>
                  <span className="px-2 py-0.5 text-[0.6875rem] font-medium italic rounded border border-slate-300 text-slate-400 dark:border-gray-600 dark:text-gray-500">
                    Loading…
                  </span>
                </span>
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {showNav && (
                <div
                  className="inline-flex items-center h-7 rounded-full border border-slate-300/80 dark:border-gray-600 bg-white/80 dark:bg-gray-900/60 shadow-sm"
                  data-testid="masterpro-nav"
                >
                  <div className="w-7 flex justify-center">
                    {hasPrev && (
                      <button
                        type="button"
                        aria-label="Previous PRO"
                        title="Previous PRO"
                        onClick={() => requestNav(siblings.pronumbers[idx - 1])}
                        className="p-1 rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <ChevronLeftIcon className="w-4 h-4" strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                  <div className="flex items-baseline gap-1.5 px-1 whitespace-nowrap">
                    <span className="text-[0.625rem] font-medium uppercase tracking-wider text-slate-400 dark:text-gray-500">
                      Master Pro
                    </span>
                    <a
                      href={masterproSearchPath(siblings.masterPro)}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="All PROs on this Master PRO (new tab)"
                      className="text-[0.75rem] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      {siblings.masterPro}
                    </a>
                    <span className="text-slate-300 dark:text-gray-600">·</span>
                    <span className="text-[0.6875rem] font-medium text-slate-500 dark:text-gray-400 tabular-nums">
                      {idx + 1} of {total}
                    </span>
                  </div>
                  <div className="w-7 flex justify-center">
                    {hasNext && (
                      <button
                        type="button"
                        aria-label="Next PRO"
                        title="Next PRO"
                        onClick={() => requestNav(siblings.pronumbers[idx + 1])}
                        className="p-1 rounded-full text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-700 transition-colors"
                      >
                        <ChevronRightIcon className="w-4 h-4" strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                </div>
              )}
              <button
                type="button"
                onClick={() => guardedClose()}
                className="p-1 text-slate-400 hover:text-slate-700 dark:text-gray-500 dark:hover:text-gray-300"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 min-h-0">
            <ProDetailContainer
              pronumber={activePro}
              mode="drawer"
              changeAll={changeAll ?? null}
              create={create ?? null}
              initialTab={initialTab}
              onClose={guardedClose}
              onCloseAfterSave={() => guardedClose({ skipOrderEditGuard: true })}
              onBadgeInfo={setBadgeInfo}
              onDirtyChange={setDirty}
              footerLeft={
                <span
                  className="text-[0.6875rem] text-slate-400 dark:text-gray-500 tabular-nums select-none"
                  title="Drawer width (debug)"
                  aria-label="Drawer width"
                >
                  {Math.round(width)}px
                </span>
              }
            />
          </div>
        </div>
      </div>
      <WarningConfirmModal
        open={pendingNav != null}
        title="Discard unsaved changes?"
        message={`You have unsaved changes on ${recordLabel} ${activePro}. Navigating to ${recordLabel} ${pendingNav ?? ''} will discard them. Continue?`}
        confirmLabel="Discard & Continue"
        cancelLabel="Stay"
        onConfirm={() => {
          if (pendingNav != null) setActivePro(pendingNav);
          setPendingNav(null);
        }}
        onCancel={() => setPendingNav(null)}
      />
      {/* Order-edit unsaved-changes guard for closing the drawer (X / Escape /
          footer Close). Invoice-tab edits now flow through the same `dirty`
          flag, so this single prompt covers them too. */}
      <WarningConfirmModal
        open={pendingClose}
        title={isCreating ? `Discard this new ${recordNoun}?` : 'Discard unsaved changes?'}
        message={
          isCreating
            ? `This ${recordNoun} has not been created yet. Closing will discard it.`
            : `You have unsaved changes on ${recordLabel} ${activePro}. Closing will discard them.`
        }
        confirmLabel="Discard & Close"
        cancelLabel="Stay"
        onConfirm={() => {
          setPendingClose(false);
          onClose();
        }}
        onCancel={() => setPendingClose(false)}
      />
      {badgeInfo.margin?.data && (
        <ProMarginModal
          open={marginModalOpen}
          onClose={() => setMarginModalOpen(false)}
          margin={badgeInfo.margin.data}
          loading={badgeInfo.marginLoading}
        />
      )}
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
};

export default ProDetailSlideOver;
